import React, { useCallback, useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { DropzoneArea } from 'mui-file-dropzone'
import styled from 'styled-components'

import FormSection from 'pages/shared/FormSection'

import ModalButton from 'components/Modal/Button'
import Spinner from 'components/Spinner'

import { PageHeader, PageTitle, screenSize, ButtonContainer, SinglePanePage } from 'styles/shared'

import GA4 from 'helpers/ga4'

import Api from 'services/api'

import useDealer from 'hooks/useDealer'

import { color } from 'styles/colors'

import WindowLocation from 'helpers/windowLocation'

const DealerImage = () => {
  const [isSaving, setIsSaving] = useState(false)
  const [loadedImage, setLoadedImage] = useState<string | null>(null)
  const { dealer, isDealerLoaded } = useDealer()

  const dealerId = dealer.id
  const { imageUrl } = dealer

  const navigate = useNavigate()

  const onClickSave = useCallback(async () => {
    GA4.sendEvent({ category: 'Dealer Logo', label: imageUrl ? 'Dealer Logo Saved' : 'Dealer Logo Uploaded', action: 'Click Save' })

    setIsSaving(true)
    if (loadedImage === null) {
      await Api.DealerImage.deleteImage(dealerId)
        .then(() => {
        }).catch(() => setIsSaving(false))
    } else {
      await Api.DealerImage.updateImage(dealerId, loadedImage)
        .then(() => {
        }).catch(() => setIsSaving(false))
    }
    navigate('/dashboard')
    WindowLocation.reload()
  }, [loadedImage, dealerId, imageUrl, navigate])

  const onFileChange = (files) => {
    const file = files[0]
    const reader = new FileReader()

    reader.onload = () => {
      const image = reader.result as string
      setLoadedImage(image)
    }

    reader.readAsDataURL(file)
  }

  const onImageRemove = () => {
    setLoadedImage(null)
  }

  useEffect(() => {
    setLoadedImage(imageUrl)
  }, [imageUrl])

  if (!isDealerLoaded) {
    return <S.Spinner variant="page" />
  }

  return (
    <>
      <PageHeader>
        <S.PageTitle>Dealer Logo</S.PageTitle>
      </PageHeader>

      <S.SinglePanePage>
        <FormSection>
          <S.DropzoneContainer hide={!!loadedImage}>
            <DropzoneArea
            fileObjects={[]}
            acceptedFiles={['image/jpeg', 'image/png', 'image/bmp', 'image/gif']}
            filesLimit={1}
            dropzoneText={'Drag and drop an image (jpeg, jpg, png, bmp, gif) here or click'}
            onDrop={files => onFileChange(files)}
            showPreviewsInDropzone={false}
            />
          </S.DropzoneContainer>
          {
            loadedImage &&
            <S.RemoveImage>
              <NavLink to="#" onClick={() => onImageRemove()}>Remove image</NavLink>
              <S.Image id="image" alt="select" src={loadedImage} />
            </S.RemoveImage>
          }
          <ButtonContainer noBorder>
            <ModalButton decoration="positive" text="Save" onPress={onClickSave} showSpinner={isSaving} />
          </ButtonContainer>
        </FormSection>
      </S.SinglePanePage>
    </>
  )
}

export default DealerImage

const S = {
  RemoveImage: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 750px;
  `,
  Image: styled.img`
    border: 1px solid ${color('border')};
    max-width: max-content;
    margin-top: 10px;
  `,
  DropzoneContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['hide'].includes(prop)
  }) <{ hide: boolean }>`
    ${({ hide }) => hide && `
      display: none;
    `}
  `,
  Spinner: styled(Spinner)`
    margin: 10px auto;
    height: 30px;
    width: 30px;
  `,
  Header: styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: revert;

    @media ${screenSize.small} {
      display: block;
    }
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
     margin-top: 0;
    }
  `
}
